import { CSSProperties, useEffect, useRef, useState } from "react";
import style from "./SearchableDropdown.module.css";
import clsx from "clsx";

import { useFormContext } from "react-hook-form";
import { Occupation } from "@/lib/DTMWebAPIClient.generated";
import { VehicleOption } from "@/slices/assetSlice";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface SearchableDropdownProps {
  options: Occupation[] | VehicleOption[];
  label: string;
  id: string;
  selectedVal: string;
  handleChange: any;
  customStyle: CSSProperties | undefined;
  disableForm: boolean;
  fetching: boolean;
  displayError?: boolean;
}

export default function SearchableDropdown({
  options,
  label,
  id,
  selectedVal,
  handleChange,
  customStyle,
  disableForm,
  fetching,
  displayError
}: SearchableDropdownProps) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const inputRef = useRef(null);
  const methods = useFormContext();
  const { ref, ...rest } = !disableForm ? methods.register("occupation", {
    required: "Search for your occupation",
  }) : {};

  useEffect(() => {
    document.addEventListener("click", toggle);
    return () => document.removeEventListener("click", toggle);
  }, []);

  const selectOption = (option: any) => {
    setQuery(() => "");
    handleChange(option[label]);
    setIsOpen((isOpen) => !isOpen);
    if (!disableForm) {
      methods.clearErrors("occupation");
      methods.setValue("occupation", option.name);
    }
  };

  function toggle(e: any) {
    setIsOpen(e && e.target === inputRef.current);
  }

  const getDisplayValue = () => {
    if (query) return query;
    if (selectedVal) return selectedVal;

    return "";
  };

  const filter = (options: any) => {
    if (options) {
      return options.filter(
        (option: any) =>
          option[label].toLowerCase().indexOf(query.toLowerCase()) > -1
      );
    } else {
      return []
    }
  };

  return (
    <div className={style.dropdown}>
      <div className={style.selectedValue}>
        {!fetching ? 
        <img
          src="assets/magnifying-glass-icon.svg"
          className={clsx(
            methods.formState?.errors?.occupation?.message || displayError ? "hidden" : "",
            "w-5 h-5 absolute top-5 md:ml-[430px]",
            style.iconImage
          )}
          alt="search"
          height={5}
          width={5}
        />
        :
        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(
          methods.formState?.errors?.occupation?.message || displayError ? "hidden" : "",
          "w-5 h-5 absolute top-5 md:ml-[430px]",
          style.iconImage
        )} />
      }
        <img
          src="assets/error-icon.svg"
          className={clsx(
            methods.formState?.errors?.occupation?.message || displayError ? "" : "hidden",
            "w-4 h-4 absolute top-5 md:ml-[430px]",
            style.iconImage
          )}
          alt="error"
          height={5}
          width={5}
        />
        <input
          ref={inputRef}
          {...rest}
          type="text"
          id="occupation"
          value={getDisplayValue()}
          onChange={(e) => {
            setQuery(e.target.value);
            handleChange(null);
            if (e.target.value == "" && !disableForm) {
              methods.setError("occupation", {
                type: "required",
                message: "Search for your occupation",
              });
            }
          }}
          onClick={toggle}
          className={clsx(
            methods.formState?.errors?.occupation?.message || displayError
              ? "error"
              : "",
            "mt-2 w:[220px] md:w-[305px]"
          )}
          style={customStyle}

        />
      </div>

      <div className={clsx(style.options, isOpen ? style.open : "")}>
        {filter(options).map((option: any, index: any) => {
          return (
            <div
              onClick={() => selectOption(option)}
              className={clsx(
                style.option,
                option[label] === selectedVal ? style.selected : ""
              )}
              key={`${id}-${index}`}
            >
              {option[label]}
            </div>
          );
        })}
      </div>
    </div>
  );
}
